import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
	AssignEmployeesBody,
	BulkPatchRoleCompetenciesResponseJSON,
	CreateRoleBody,
	ListRolesResponseJSON,
	PatchRoleCompetencyBody,
	RoleCompetenciesResponseJSON,
	RoleEmployeesResponseJSON,
	SingleRoleResponseJSON,
	UpdateRoleBody,
} from "@performyard/service-contracts";

import {
	BulkPatchRoleCompetenciesRequest,
	ListRoleEmployeesParams,
	ListRolesParams,
	archiveRole,
	assignRoleEmployees,
	bulkPatchRoleCompetencies,
	checkRoleName,
	createRole,
	getRole,
	getRoleCompetencies,
	getRoleEmployees,
	getRoles,
	patchRoleCompetency,
	removeRoleEmployee,
	reopenRole,
	updateRole,
} from "../../services/defs/roles";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

// Query keys
export const rolesKeys = {
	all: ["Roles"] as const,
	list: (params?: ListRolesParams) => [...rolesKeys.all, "list", params] as const,
	detail: (id: string) => [...rolesKeys.all, "detail", id] as const,
	checkName: (name: string, excludeId?: string) => [...rolesKeys.all, "checkName", name, excludeId] as const,
	competencies: (roleId: string) => [...rolesKeys.all, "competencies", roleId] as const,
	employees: (roleId: string, params?: ListRoleEmployeesParams) =>
		[...rolesKeys.all, "employees", roleId, params] as const,
};

// List roles
export const useRoles = (params: ListRolesParams = {}, options?: PYUseQueryOptions<ListRolesResponseJSON>) => {
	return useQuery({
		queryKey: rolesKeys.list(params),
		queryFn: () => getRoles(params),
		meta: {
			errorMessage: "There was an error getting the roles.",
		},
		...options,
	});
};

// Get single role
export const useRole = (id: string, options?: PYUseQueryOptions<SingleRoleResponseJSON>) => {
	return useQuery({
		queryKey: rolesKeys.detail(id),
		queryFn: () => getRole(id),
		enabled: !!id,
		meta: {
			errorMessage: "There was an error getting the role.",
		},
		...options,
	});
};

// Spread caller options FIRST so our mutationFn + onSuccess can't be silently overridden;
// the wrapped onSuccess runs the default cache management before delegating to the caller's.
export const useCreateRole = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		...options,
		mutationFn: (body: CreateRoleBody) => createRole(body),
		onSuccess: (data, vars, onMutateResult, ctx) => {
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
};

export const useUpdateRole = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		...options,
		mutationFn: ({ id, body }: { id: string; body: UpdateRoleBody }) => updateRole(id, body),
		onSuccess: (data, vars, onMutateResult, ctx) => {
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
};

export const useArchiveRole = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		...options,
		mutationFn: (id: string) => archiveRole(id),
		onSuccess: (data, vars, onMutateResult, ctx) => {
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
};

export const useReopenRole = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		...options,
		mutationFn: (id: string) => reopenRole(id),
		onSuccess: (data, vars, onMutateResult, ctx) => {
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
};

// Check role name availability
export const useCheckRoleName = (
	name: string,
	excludeId?: string,
	options?: PYUseQueryOptions<{ available: boolean }>
) => {
	return useQuery({
		queryKey: rolesKeys.checkName(name, excludeId),
		queryFn: () => checkRoleName(name, excludeId),
		enabled: !!name,
		meta: {
			errorMessage: "There was an error checking the role name.",
		},
		...options,
	});
};

// Joined competency details for the role detail page
export const useRoleCompetencies = (roleId: string, options?: PYUseQueryOptions<RoleCompetenciesResponseJSON>) => {
	return useQuery({
		queryKey: rolesKeys.competencies(roleId),
		queryFn: () => getRoleCompetencies(roleId),
		enabled: !!roleId,
		meta: {
			errorMessage: "There was an error getting the role's competencies.",
		},
		...options,
	});
};

// Single-competency mutation (upsert/remove). Backend returns the updated role doc, so we
// write it straight into the detail cache and skip a refetch round-trip. The joined
// competency view (different endpoint) and the list (count column) still need invalidation.
export const usePatchRoleCompetency = (roleId: string, options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		...options,
		mutationFn: (body: PatchRoleCompetencyBody) => patchRoleCompetency(roleId, body),
		onSuccess: (data, vars, onMutateResult, ctx) => {
			queryClient.setQueryData(rolesKeys.detail(roleId), data);
			queryClient.invalidateQueries({ queryKey: rolesKeys.competencies(roleId) });
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
};

// Bulk expected-level mutation (inline edit mode). Unlike the single-item mutation above, the
// response is a successful/failed id list, not the updated role doc — invalidate and let the
// joined competencies view refetch instead of writing into the cache by hand.
export const useBulkPatchRoleCompetencies = (
	roleId: string,
	options?: PYUseMutationOptions<BulkPatchRoleCompetenciesResponseJSON, unknown, BulkPatchRoleCompetenciesRequest>
) => {
	const queryClient = useQueryClient();
	return useMutation({
		...options,
		mutationFn: (body: BulkPatchRoleCompetenciesRequest) => bulkPatchRoleCompetencies(roleId, body),
		onSuccess: (data, vars, onMutateResult, ctx) => {
			queryClient.invalidateQueries({ queryKey: rolesKeys.competencies(roleId) });
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
};

// Joined employee list for the role detail page
export const useRoleEmployees = (
	roleId: string,
	params: ListRoleEmployeesParams = {},
	options?: PYUseQueryOptions<RoleEmployeesResponseJSON>
) => {
	return useQuery({
		queryKey: rolesKeys.employees(roleId, params),
		queryFn: () => getRoleEmployees(roleId, params),
		enabled: !!roleId,
		meta: {
			errorMessage: "There was an error getting the role's employees.",
		},
		...options,
	});
};

export const useAssignRoleEmployees = (roleId: string, options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		...options,
		mutationFn: (body: AssignEmployeesBody) => assignRoleEmployees(roleId, body),
		onSuccess: (data, vars, onMutateResult, ctx) => {
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
};

export const useRemoveRoleEmployee = (roleId: string, options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		...options,
		mutationFn: (userId: string) => removeRoleEmployee(roleId, userId),
		onSuccess: (data, vars, onMutateResult, ctx) => {
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
			options?.onSuccess?.(data, vars, onMutateResult, ctx);
		},
	});
};
